import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function HtaccessGenerator() {
  const [https, setHttps] = useState(true);
  const [www, setWww] = useState(true);
  const [cache, setCache] = useState(true);

  let out = "";
  if (https) out += "RewriteEngine On\nRewriteCond %{HTTPS} off\nRewriteRule ^(.*)$ https://%{HTTP_HOST}%{REQUEST_URI} [L,R=301]\n\n";
  if (www) out += "RewriteCond %{HTTP_HOST} !^www\\. [NC]\nRewriteRule ^(.*)$ https://www.%{HTTP_HOST}/$1 [L,R=301]\n\n";
  if (cache) out += "<IfModule mod_expires.c>\n  ExpiresActive On\n  ExpiresByType image/jpg \"access plus 1 year\"\n  ExpiresByType image/png \"access plus 1 year\"\n  ExpiresByType text/css \"access plus 1 month\"\n  ExpiresByType application/javascript \"access plus 1 month\"\n</IfModule>";

  return (
    <ToolPageLayout
      title=".htaccess Generator"
      description="Generate standard directives for Apache .htaccess file including HTTPS, WWW redirects, and browser caching."
      h1=".htaccess Generator"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Toggle your desired .htaccess rules using the checkboxes.",
        "Copy the generated .htaccess content and place it in your website root."
      ]}
      faq={[
        { question: "What is an .htaccess file?", answer: "An .htaccess file is a configuration file used by Apache web servers to enable/disable functionality like redirection or security settings." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <label className="flex items-center gap-2"><input type="checkbox" checked={https} onChange={e => setHttps(e.target.checked)} /> Force HTTPS</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={www} onChange={e => setWww(e.target.checked)} /> Force WWW</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cache} onChange={e => setCache(e.target.checked)} /> Browser Caching</label>
        </div>
        <Textarea value={out} readOnly className="min-h-[300px] font-mono text-sm bg-slate-50" />
      </div>
    </ToolPageLayout>
  );
}

export function RobotsTxtGenerator() {
  const [all, setAll] = useState(true);
  const [sitemap, setSitemap] = useState("");

  let out = `User-agent: *\nDisallow: ${all ? "" : "/"}\n`;
  if (sitemap) out += `\nSitemap: ${sitemap}`;

  return (
    <ToolPageLayout
      title="robots.txt Generator"
      description="Create an SEO-friendly robots.txt file to instruct search engines how to crawl your website."
      h1="robots.txt Generator"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Configure crawl permissions and sitemap details.",
        "Copy the generated robots.txt file and place it in your root directory."
      ]}
      faq={[
        { question: "What is a robots.txt file?", answer: "It is a text file webmasters create to instruct search engine bots on how to crawl pages on their website." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <label className="flex items-center gap-2"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> Allow all crawlers</label>
          <div className="space-y-2"><Label>Sitemap URL</Label><Input value={sitemap} onChange={e => setSitemap(e.target.value)} placeholder="https://example.com/sitemap.xml" /></div>
        </div>
        <Textarea value={out} readOnly className="min-h-[300px] font-mono text-sm bg-slate-50" />
      </div>
    </ToolPageLayout>
  );
}

export function OpenGraphPreview() {
  const [title, setTitle] = useState("Awesome Title");
  const [desc, setDesc] = useState("This is a description of the webpage.");
  const [img, setImg] = useState("https://via.placeholder.com/1200x630");

  return (
    <ToolPageLayout
      title="Open Graph Preview Checker"
      description="Preview how your webpage will look when shared on Facebook, LinkedIn, Twitter, and other social media platforms."
      h1="Open Graph Preview"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Fill in the page title, description, and social sharing image URL.",
        "Check the real-time social media card preview on the right."
      ]}
      faq={[
        { question: "What is Open Graph?", answer: "Open Graph is a protocol created by Facebook that allows any web page to become a rich object in a social graph." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea value={desc} onChange={e => setDesc(e.target.value)} /></div>
          <div className="space-y-2"><Label>Image URL</Label><Input value={img} onChange={e => setImg(e.target.value)} /></div>
        </div>
        <div>
          <Label className="mb-2 block">Social Media Preview Card</Label>
          <div className="border border-slate-200 rounded-lg overflow-hidden max-w-[500px] shadow-sm">
            <img src={img} alt="OG" className="w-full h-[260px] object-cover bg-slate-100" referrerPolicy="no-referrer" />
            <div className="p-4 bg-slate-50">
              <h4 className="font-bold text-slate-900 truncate">{title}</h4>
              <p className="text-sm text-slate-600 line-clamp-2 mt-1">{desc}</p>
              <div className="text-xs text-slate-400 mt-2 uppercase">247devtoolshub.online</div>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function FaviconGenerator() {
  const [text, setText] = useState("🚀");
  const [bg, setBg] = useState("#3b82f6");
  const [color, setColor] = useState("#ffffff");
  const [size, setSize] = useState(256);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const generateFavicon = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    // Clear
    ctx.clearRect(0, 0, size, size);
    
    // Background
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.arc(size/2, size/2, size/2, 0, Math.PI * 2);
    ctx.fill();
    
    // Text/Emoji
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${size * 0.5}px Arial`;
    ctx.fillText(text.substring(0, 2), size/2, size/2 + size * 0.05); // slight vertical offset for emojis
  };

  useEffect(() => {
    generateFavicon();
  }, [text, bg, color, size]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "favicon.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <ToolPageLayout
      title="Favicon Generator Online"
      description="Create beautifully styled round favicons using custom text or emojis with our free online tool."
      h1="Favicon Generator"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Type 1-2 characters or a single emoji.",
        "Choose custom background and text colors.",
        "Click Download Favicon to save a 256x256 high-res PNG."
      ]}
      faq={[
        { question: "What sizes are recommended for favicons?", answer: "Modern browsers support 16x16, 32x32, 48x48, or PNGs up to 256x256 for high-DPI displays." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8 items-center max-w-2xl mx-auto">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Emoji or Text (Max 2 chars)</Label>
            <Input value={text} onChange={e => setText(e.target.value)} maxLength={2} />
          </div>
          <div className="space-y-2">
            <Label>Background Color</Label>
            <div className="flex gap-2">
              <Input value={bg} onChange={e => setBg(e.target.value)} />
              <input type="color" value={bg} onChange={e => setBg(e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Text Color</Label>
            <div className="flex gap-2">
              <Input value={color} onChange={e => setColor(e.target.value)} />
              <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
            </div>
          </div>
          <Button onClick={download} className="w-full">Download Favicon</Button>
        </div>
        <div className="flex justify-center p-8 bg-slate-50 border rounded-xl">
          <canvas 
            ref={canvasRef} 
            width={size} 
            height={size} 
            className="w-32 h-32 border shadow-sm rounded-full" 
          />
        </div>
      </div>
    </ToolPageLayout>
  );
}
